
import { Card } from "primereact/card";
import { Paginator } from "primereact/paginator";
import { Skeleton } from "primereact/skeleton";
import { useNavigate } from "react-router-dom";
import useUrlState from "@ahooksjs/use-url-state";
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import MeaButton from "../../../components/MeaButton";
import trainingsAPI from "../../../services/trainings";
import CreateTrainingModal from "./CreateTrainingModal";
import MeaLink from "../../../components/MeaLink";
import { PERMISSION_SCHOOL_TRAININGS } from "../../../permissions";
import { useAuthContext } from "../../../hooks/useAuthContext";
import { useParams } from "react-router-dom";
import UpdateTrainingModal from "../info/components/UpdateTrainingModal";
import { Tag } from "primereact/tag";

const TrainingsCardsComponent = forwardRef((props, ref) => {
  const { showAddButton } = props;
  const { auth, currentMembership, hasPermission } = useAuthContext();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12); // Reduced for cards layout

  const params = useParams();
  const schoolId = params.schoolId;
  const accessToken = "";
  
  const createModalRef = useRef();
  const updateModalRef = useRef();
  
  const [state, setState] = useUrlState({
    trainingName: "",
    trainingStatus: "",
  });
  const [training, setTraining] = useState(null);
  
  const queryList = trainingsAPI.useGetList(
    schoolId,
    accessToken,
    page,
    pageSize,
    state.trainingStatus
  );
  
  useImperativeHandle(ref, () => ({
    refresh: () => {
      queryList?.refetch();
    },
  }));

  const onPageChange = (event) => {
    setPage(event.page + 1);
    setPageSize(event.rows);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "OPEN":
        return "success";
      case "DRAFT":
        return "info";
      case "SUSPENDED":
        return "warning";
      case "ARCHIVED":
        return "contrast";
      default:
        return null;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "OPEN":
        return "Ouvert";
      case "DRAFT":
        return "Brouillon";
      case "SUSPENDED":
        return "Suspendu";
      case "ARCHIVED":
        return "Archivé";
      default:
        return status;
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case "OPEN":
        return "bg-green-50";
      case "DRAFT":
        return "bg-blue-50";
      case "SUSPENDED":
        return "bg-red-50";
      case "ARCHIVED":
        return "bg-gray-50";
      default:
        return "bg-white";
    }
  };

  const startToolbarTemplate = (
    <div>
      {showAddButton && (
        <MeaButton
          label="Créer une formation"
          icon="pi pi-plus"
          size="normal"
          className="p-button mr-2 action mt-2 mb-5 ml-2"
          onClick={() => {
            createModalRef.current.show(() => {
              queryList.refetch();
            });
          }}
          data-pr-tooltip="Nouvelle formation"
          permission={PERMISSION_SCHOOL_TRAININGS.CREATE}
        />
      )}
    </div>
  );

  if (queryList.isLoading) {
    return (
      <>
        {startToolbarTemplate}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, index) => (
            <Card key={index} className="p-4">
              <div className="space-y-3">
                {/* Status tag skeleton */}
                <div className="flex justify-between items-start">
                  <Skeleton width="4rem" height="1.5rem" />
                </div>
                
                {/* Title skeleton */}
                <Skeleton width="100%" height="1.5rem" className="mb-2" />
                
                {/* Presentation skeleton */}
                <Skeleton width="100%" height="1rem" className="mb-2" />
                
                {/* Date fields skeleton */}
                <div className="space-y-2">
                  <div className="flex items-center">
                    <Skeleton width="8rem" height="1rem" className="mr-2" />
                    <Skeleton width="6rem" height="1rem" />
                  </div>
                  <div className="flex items-center">
                    <Skeleton width="7rem" height="1rem" className="mr-2" />
                    <Skeleton width="6rem" height="1rem" />
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </>
    );
  }

  if (queryList.isError) {
    return (
      <>
        {startToolbarTemplate}
        <div className="text-center p-8">
          <p className="text-red-500">Erreur lors du chargement des formations</p>
          <MeaButton onClick={() => queryList.refetch()} className="mt-4">
            Réessayer
          </MeaButton>
        </div>
      </>
    );
  }

  const trainings = queryList.data?.data || [];
  const totalRecords = queryList.data?.total || 0;

  return (
    <>
      <CreateTrainingModal ref={createModalRef} schoolId={schoolId} accessToken={accessToken} />
      <UpdateTrainingModal ref={updateModalRef} schoolId={schoolId} training={training} query={queryList} accessToken={accessToken} />
      
      {startToolbarTemplate}
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
        {trainings.map((item) => (
          <Card 
            key={item.id} 
            className={`${getStatusBgColor(item.status)} hover:shadow-lg transition-shadow duration-200 cursor-pointer`}
            onClick={() => {
              // Navigate to the training detail page using React Router
              navigate(`/school/${schoolId}/admin/trainings/${item.id}/info`);
            }}
          >
            <div className="p-4">
              <div className="flex justify-between items-start mb-3">
                <Tag
                  value={getStatusLabel(item.status)}
                  severity={getStatusColor(item.status)}
                  className="text-xs"
                />
                <MeaButton
                  icon="pi pi-pencil"
                  rounded
                  text
                  size="small"
                  aria-label="Modifier"
                  onClick={(e) => {
                    e.stopPropagation();
                    setTraining(item);
                    if (updateModalRef.current) {
                      updateModalRef.current.show(() => {
                        queryList.refetch();
                      });
                    }
                  }}
                  permission={PERMISSION_SCHOOL_TRAININGS.UPDATE}
                />
              </div>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-3 line-clamp-2 cursor-pointer hover:text-primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/school/${schoolId}/admin/trainings/${item.id}/info`);
                  }}>
                {item.name}
              </h3>
              
              {item.presentation && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-3">
                  {item.presentation}
                </p>
              )}
              
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center">
                  <span className="font-medium mr-2">Début:</span>
                  <span>
                    {item?.start_courses_date 
                      ? new Date(item.start_courses_date).toLocaleDateString("fr-FR")
                      : "-"
                    }
                  </span>
                </div>
                
                <div className="flex items-center">
                  <span className="font-medium mr-2">Fin:</span>
                  <span>
                    {item?.end_courses_date 
                      ? new Date(item.end_courses_date).toLocaleDateString("fr-FR")
                      : "-"
                    }
                  </span>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {totalRecords > pageSize && (
        <div className="flex justify-center">
          <Paginator
            first={(page - 1) * pageSize}
            rows={pageSize}
            totalRecords={totalRecords}
            onPageChange={onPageChange}
            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
            className="border rounded-lg p-2"
          />
        </div>
      )}
    </>
  );
});

export default TrainingsCardsComponent;
